import React, { createContext, useState, useCallback, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DynamicIcon } from '../components/icons.jsx';

export const NotificationContext = createContext(null);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        console.warn("useNotification must be used within a NotificationProvider");
        return { addNotification: () => { } };
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const removeNotification = useCallback((id) => {
        setNotifications(current => current.filter(n => n.id !== id));
    }, []);

    const addNotification = useCallback((message, type = 'success') => {
        const id = Date.now();
        setNotifications(current => [...current, { id, message, type }]);

        // Auto-dismiss: success/info after 4s, error/warning after 8s (safety net — never permanently stuck)
        const delay = (type === 'success' || type === 'info') ? 4000 : 8000;
        setTimeout(() => {
            setNotifications(current => current.filter(n => n.id !== id));
        }, delay);
    }, []);

    const notificationIcons = {
        success: { icon: 'CheckCircle', color: 'text-green-500' },
        error: { icon: 'XCircle', color: 'text-red-500' },
        warning: { icon: 'AlertTriangle', color: 'text-yellow-500' },
        info: { icon: 'Info', color: 'text-blue-500' },
    };

    return (
        <NotificationContext.Provider value={{ addNotification, removeNotification }}>
            {children}
            <div className="fixed inset-x-0 top-0 p-4 flex flex-col items-center space-y-2 z-[9999] pointer-events-none">
                <AnimatePresence>
                    {notifications.map(notification => {
                        const { icon, color } = notificationIcons[notification.type] || notificationIcons.info;
                        return (
                            <motion.div
                                key={notification.id}
                                layout
                                initial={{ opacity: 0, y: -50, scale: 0.5 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
                                className="w-full max-w-md bg-white shadow-2xl rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5"
                            >
                                <div className="flex-1 w-0 p-4">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0 pt-0.5">
                                            <DynamicIcon name={icon} className={`w-6 h-6 ${color}`} />
                                        </div>
                                        <div className="ml-3 flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                                {notification.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex border-l border-gray-200">
                                    <button
                                        onClick={() => removeNotification(notification.id)}
                                        className="w-full border border-transparent rounded-none rounded-r-xl p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500 focus:outline-none"
                                    >
                                        <DynamicIcon name="X" className="w-5 h-5" />
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </NotificationContext.Provider>
    );
};